import React, { useState, useCallback } from 'react'
import { 
    HiOutlineChevronDoubleLeft,
    HiOutlineDotsHorizontal, 
    HiChevronDoubleRight 
} from 'react-icons/hi'

const baseItemStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    height: '32px',
    padding: '0 6px',
    borderRadius: '8px',
    cursor: 'pointer',
    userSelect: 'none',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: '13px',
    lineHeight: '25px',
    letterSpacing: '0',
    border: 'none',
    listStyle: 'none',
    transition: 'none',
}

const activeItemStyle = {
    ...baseItemStyle,
    backgroundColor: '#F3F4F6',
    color: '#6366F1',
}

const inactiveItemStyle = {
    ...baseItemStyle,
    backgroundColor: 'transparent',
    color: '#111827',
}

const dotsStyle = {
    ...baseItemStyle,
    backgroundColor: 'transparent',
    color: '#111827',
    cursor: 'default',
    minWidth: '24px',
}

// ── Dots ──────────────────────────────────────────────────────────────────────
const NextMore = ({ onArrow }) => {
    const [arrow, setArrow] = useState(false)
    return (
        <li
            style={{ ...dotsStyle, cursor: 'pointer' }}
            onMouseEnter={() => setArrow(true)}
            onMouseLeave={() => setArrow(false)}
            onClick={() => onArrow('nextMore')}
        >
            {arrow
                ? <HiChevronDoubleRight style={{ width: 13, height: 13 }} />
                : <HiOutlineDotsHorizontal style={{ width: 13, height: 13 }} />
            }
        </li>
    )
}

const PrevMore = ({ onArrow }) => {
    const [arrow, setArrow] = useState(false)
    return (
        <li
            style={{ ...dotsStyle, cursor: 'pointer' }}
            onMouseEnter={() => setArrow(true)}
            onMouseLeave={() => setArrow(false)}
            onClick={() => onArrow('prevMore')}
        >
            {arrow
                ? <HiOutlineChevronDoubleLeft style={{ width: 13, height: 13 }} />
                : <HiOutlineDotsHorizontal style={{ width: 13, height: 13 }} />
            }
        </li>
    )
}

// ── Page item ─────────────────────────────────────────────────────────────────
const PageItem = ({ page, currentPage, onClick }) => (
    <li
        style={currentPage === page ? activeItemStyle : inactiveItemStyle}
        onClick={e => onClick(page, e)}
        tabIndex="0"
    >
        {page}
    </li>
)

// ── Pagers ────────────────────────────────────────────────────────────────────
const Pagers = ({ pageCount, currentPage, onChange }) => {

    const onPagerClick = (value, e) => {
        e.preventDefault()
        const newPage = Math.min(Math.max(value, 1), pageCount)
        if (newPage !== currentPage) onChange(newPage)
    }

    const onArrowClick = useCallback(dir => {
        const newPage = dir === 'nextMore'
            ? Math.min(currentPage + 5, pageCount)
            : Math.max(currentPage - 5, 1)
        onChange(newPage)
    }, [currentPage, onChange, pageCount])

    // ── Render logic: always 1 2 3 ... last ──────────────────────────────────
    const renderPages = () => {
        if (!pageCount || pageCount <= 0) return null

        // 4 or fewer pages — show all, no dots
        if (pageCount <= 4) {
            return Array.from({ length: pageCount }, (_, i) => i + 1).map(page => (
                <PageItem key={page} page={page} currentPage={currentPage} onClick={onPagerClick} />
            ))
        }

        const isNearStart = currentPage <= 3
        const isNearEnd   = currentPage >= pageCount - 2

        // Near start: 1 2 3 ... last
        if (isNearStart) {
            return (
                <>
                    <PageItem page={1} currentPage={currentPage} onClick={onPagerClick} />
                    <PageItem page={2} currentPage={currentPage} onClick={onPagerClick} />
                    <PageItem page={3} currentPage={currentPage} onClick={onPagerClick} />
                    <NextMore onArrow={onArrowClick} />
                    <PageItem page={pageCount} currentPage={currentPage} onClick={onPagerClick} />
                </>
            )
        }

        // Near end: 1 ... last-2 last-1 last
        if (isNearEnd) {
            return (
                <>
                    <PageItem page={1} currentPage={currentPage} onClick={onPagerClick} />
                    <PrevMore onArrow={onArrowClick} />
                    <PageItem page={pageCount - 2} currentPage={currentPage} onClick={onPagerClick} />
                    <PageItem page={pageCount - 1} currentPage={currentPage} onClick={onPagerClick} />
                    <PageItem page={pageCount}     currentPage={currentPage} onClick={onPagerClick} />
                </>
            )
        }

        // Middle: 1 ... cur ... last
        return (
            <>
                <PageItem page={1}           currentPage={currentPage} onClick={onPagerClick} />
                <PrevMore onArrow={onArrowClick} />
                <PageItem page={currentPage} currentPage={currentPage} onClick={onPagerClick} />
                <NextMore onArrow={onArrowClick} />
                <PageItem page={pageCount}   currentPage={currentPage} onClick={onPagerClick} />
            </>
        )
    }

    return (
        <ul style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            listStyle: 'none',
            margin: 0,
            padding: 0,
        }}>
            {renderPages()}
        </ul>
    )
}

export default Pagers